package ui.widget

import androidx.compose.animation.*
import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.text.style.*
import androidx.compose.ui.unit.*
import compose.assets.*
import compose.assets.svg.*
import kotlinx.datetime.*


import ssoft.frontend.generated.resources.*
import tools.*


@Composable
fun HourRangePickerDialog(
    state: DialogState,
    start: String,
    end: String,
    onDeleteAction: (() -> Unit)? = null,
    onChange: (start: LocalTime, end: LocalTime) -> Unit
) {
    Dialog(state) {
        val (startState, endState) = HourRangeState.rememberState(start, end)
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            title = {
                Row {
                    Text(
                        text = "${startState.rememberAsTimeStart()} - ${endState.rememberAsTimeEnd()}",
                        modifier = Modifier.align(Alignment.CenterVertically),
                    )
                    onDeleteAction?.also { onDelete ->
                        Spacer(Modifier.weight(1F))
                        IconButton(
                            modifier = Modifier.align(Alignment.CenterVertically),
                            onClick = {
                                state.hide()
                                onDelete()
                            }) {
                            Icon(Svg.Delete_outline, null)
                        }
                    }
                }
            },
            onDismissRequest = { state.hide() },
            dismissButton = {
                TextButton({ state.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            },
            confirmButton = {
                TextButton({
                    onChange(
                        startState.asTimeStart(),
                        endState.asTimeEnd()
                    )
                    state.hide()
                }) {
                    Text(Res.string.dialog__action_ok.rememberString())
                }
            }) {
            HourRange(startState, endState)
        }
    }
}

private data class TimeEntry(
    val hour: Int,
    val label: String = hour.toString().padStart(2, '0'),
) {
    companion object {

        val items = (0..23).map {
            TimeEntry(it)
        }

        val itemsChunked = items.chunked(6)
    }
}

@Stable
data class HourRangeState(val initialHour: Int) {

    fun select(index: Int) {
        hour = index
    }

    var hour by mutableStateOf(initialHour)
        private set

    fun asTimeEnd() = LocalTime.parse("${hour.toString().padStart(2, '0')}:59")

    fun asTimeStart() = LocalTime.parse("${hour.toString().padStart(2, '0')}:00")

    @Composable
    fun rememberAsTimeEnd() = remember(hour) { asTimeEnd() }

    @Composable
    fun rememberAsTimeStart() = remember(hour) { asTimeStart() }

    companion object {
        @Composable
        fun rememberState(start: String, end: String): Pair<HourRangeState, HourRangeState> {
            return remember(start, end) {
                val st = LocalTime.parse(start).let {
                    HourRangeState(it.hour)
                }
                val en = LocalTime.parse(end).let {
                    HourRangeState(it.hour)
                }

                st to en
            }
        }

        @Composable
        fun rememberState(start: LocalTime, end: LocalTime): Pair<HourRangeState, HourRangeState> {
            return remember(start, end) {
                val st = HourRangeState(start.hour)
                val en = HourRangeState(end.hour)

                st to en
            }
        }
    }
}

@Composable
fun HourRange(
    start: HourRangeState,
    end: HourRangeState,
    modifier: Modifier = Modifier
) {
    Column(modifier) {
        TimeEntry.itemsChunked.forEach { row ->
            Space(8.dp)
            Row {
                row.forEach {
                    Item(it, start, end)
                }
            }
        }
    }
}

@Composable
private fun RowScope.Item(item: TimeEntry, start: HourRangeState, end: HourRangeState) {

    val bgSelection = animateColorAsState(
        animationSpec = tween(100),
        targetValue =
        if (item.hour == start.hour || item.hour == end.hour) MaterialTheme.colorScheme.primary
        else Color.Transparent
    )
    val bgRangeStart = animateColorAsState(
        animationSpec = tween(100),
        targetValue =
        if (start.hour == item.hour) Color.Transparent
        else if (start.hour != end.hour && item.hour in (start.hour)..(end.hour)) MaterialTheme.colorScheme.secondaryContainer
        else Color.Transparent
    )
    val bgRangeEnd = animateColorAsState(
        animationSpec = tween(100),
        targetValue =
        if (end.hour == item.hour) Color.Transparent
        else if (start.hour != end.hour && item.hour in (start.hour)..(end.hour)) MaterialTheme.colorScheme.secondaryContainer
        else Color.Transparent
    )
    Box(
        Modifier.weight(1F)
            .height(48.dp)
    ) {
        Row(Modifier.fillMaxSize()) {
            Spacer(
                Modifier
                    .weight(1F)
                    .fillMaxHeight()
                    .background(bgRangeStart.value)
            )
            Spacer(
                Modifier
                    .weight(1F)
                    .fillMaxHeight()
                    .background(bgRangeEnd.value)
            )
        }
        Box(Modifier
            .align(Alignment.Center)
            .size(48.dp)
            .clip(CircleShape)
            .background(bgSelection.value)
            .clickable {
                if (start.hour == end.hour) {
                    if (item.hour < start.hour) {
                        start.select(item.hour)
                    } else if (item.hour > start.hour) {
                        end.select(item.hour)
                    }
                } else {
                    start.select(item.hour)
                    end.select(item.hour)
                }
            }) {
            Text(
                text = item.label,
                color = contentColorFor(bgSelection.value),
                textAlign = TextAlign.Center,
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.align(Alignment.Center)
            )
        }

    }
}





